import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';
import {
  YdsButtonComponent,
  YdsButtonBackComponent,
  YdsCardEditableComponent,
  YdsDropdownComponent,
  YdsSpinnerComponent,
  YdsSwitchComponent,
  YdsTextAreaComponent,
  YdsTextFieldComponent,
  type YdsDropdownOption,
} from '../../../yds-angular';
import { JobProcess } from '../../../model/job-process.model';
import { JobprocessService } from '../jobprocess.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { LoginInformationService } from '../../../locallogin/login-information.service';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Component({
  selector: 'app-jobprocess-editable',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    YdsCardEditableComponent,
    YdsButtonComponent,
    YdsButtonBackComponent,
    YdsSpinnerComponent,
    YdsSwitchComponent,
    YdsDropdownComponent,
    YdsTextAreaComponent,
    YdsTextFieldComponent,
  ],
  templateUrl: './jobprocess-editable.component.html',
  styleUrl: './jobprocess-editable.component.scss',
})
export class JobprocessEditableComponent {
  private formBuilder = inject(FormBuilder);
  private jobprocessService = inject(JobprocessService);
  private dialogService = inject(ConfirmDialogService);
  private loginInformationService = inject(LoginInformationService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  /** Id of the job being edited, or null in add mode. */
  readonly jobProcessId = signal<string | null>(null);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly notFound = signal(false);
  readonly detailsEditing = signal(false);

  /** The last loaded/saved record, used for the read-only view and to revert on cancel. */
  private readonly record = signal<JobProcess | null>(null);

  /** Whether we are creating a new job (no id in the route). */
  readonly isAddMode = computed(() => this.jobProcessId() === null);

  /** Running jobs cannot be deleted. */
  readonly isRunning = computed(() => this.record()?.status === 'Running');

  readonly statusOptions: readonly YdsDropdownOption[] = [
    { id: 'Waiting', label: 'Waiting' },
    { id: 'Running', label: 'Running' },
    { id: 'Done', label: 'Done' },
    { id: 'Error', label: 'Error' },
  ];

  /** Options for the "execCmd" dropdown, loaded from the JobNames endpoint. */
  readonly jobNameOptions = signal<YdsDropdownOption[]>([]);

  readonly form: FormGroup = this.formBuilder.group({
    websiteInstanceId: [''],
    name: ['', Validators.required],
    description: [''],
    tenantCode: [null as number | null],
    parameters: [''],
    notRestartable: [false],
    user: [''],
    execCmd: [null as string | null, Validators.required],
    status: [null as string | null, Validators.required],
    errorMessage: [''],
    result: [''],
    lastRunned: [null as string | null],
    dateStart: [null as string | null],
    dateEnd: [null as string | null],
  });

  constructor() {
    const id = this.route.snapshot.paramMap.get('id');
    this.loadJobNames();

    if (id) {
      this.jobProcessId.set(id);
      this.loadJob(id);
      // Deep-link straight into edit mode from the list.
      if (this.route.snapshot.queryParamMap.get('edit') === '1') {
        this.detailsEditing.set(true);
      }
    } else {
      this.form.patchValue({ status: 'Waiting' });
      this.setDefaultTenantCode();
      this.prefillNameFromExecCmd();
      this.detailsEditing.set(true);
    }
  }

  private loadJob(id: string): void {
    this.isLoading.set(true);
    this.jobprocessService
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (job) => {
          if (!job) {
            this.notFound.set(true);
          } else {
            this.record.set(job);
            this.patchForm(job);
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.notFound.set(true);
          this.isLoading.set(false);
        },
      });
  }

  private loadJobNames(): void {
    this.jobprocessService
      .getJobNames()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((names) => {
        this.jobNameOptions.set(names.map((name) => ({ id: name, label: name })));
      });
  }

  /** Defaults tenantCode to the logged-in user's tenant when adding a new job. */
  private setDefaultTenantCode(): void {
    this.loginInformationService
      .getUserInfo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((info) => {
        if (info?.tenant_id != null) {
          this.form.patchValue({ tenantCode: info.tenant_id });
        }
      });
  }

  /** Copies the selected execCmd into name while name is still empty. */
  private prefillNameFromExecCmd(): void {
    this.form
      .get('execCmd')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((execCmd: string | null) => {
        const nameControl = this.form.get('name');
        if (execCmd && !nameControl?.value) {
          nameControl?.setValue(execCmd);
        }
      });
  }

  private patchForm(job: JobProcess): void {
    this.form.patchValue({
      websiteInstanceId: job.websiteInstanceId ?? '',
      name: job.name ?? '',
      description: job.description ?? '',
      tenantCode: job.tenantCode ?? null,
      parameters: job.parameters ?? '',
      notRestartable: job.notRestartable ?? false,
      user: job.user ?? '',
      execCmd: job.execCmd ?? null,
      status: job.status ?? null,
      errorMessage: job.errorMessage ?? '',
      result: job.result ?? '',
      lastRunned: job.lastRunned ?? null,
      dateStart: job.dateStart ?? null,
      dateEnd: job.dateEnd ?? null,
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  // --- View helpers -------------------------------------------------------------

  readonly job = computed(() => this.record());

  /** Formats an ISO date/time value for the read-only view. */
  formatDateTime(value: string | null): string {
    if (!value) {
      return 'n.v.t.';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'n.v.t.';
    }
    return new Intl.DateTimeFormat('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  }

  /** Returns the value for display, or 'n.v.t.' when empty. */
  displayValue(value: string | number | boolean | null): string {
    if (value === null || value === undefined || value === '') {
      return 'n.v.t.';
    }
    return String(value);
  }

  copyToClipboard(value: string | null): void {
    if (!value) {
      return;
    }
    void navigator.clipboard?.writeText(value);
  }

  /** Sets a date/time control to the current moment. */
  setFieldNow(controlName: string): void {
    const control = this.form.get(controlName);
    control?.setValue(new Date().toISOString());
    control?.markAsDirty();
  }

  /** Clears a date/time control. */
  clearField(controlName: string): void {
    const control = this.form.get(controlName);
    control?.setValue(null);
    control?.markAsDirty();
  }

  // --- Details card actions -----------------------------------------------------

  onDetailsCancel(): void {
    if (this.isAddMode()) {
      this.goBack();
      return;
    }

    const current = this.record();
    if (current) {
      this.patchForm(current);
    }
    this.detailsEditing.set(false);
  }

  onDetailsNext(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value as {
      websiteInstanceId: string;
      name: string;
      description: string;
      tenantCode: number | null;
      parameters: string;
      notRestartable: boolean;
      user: string;
      execCmd: string | null;
      status: string | null;
      errorMessage: string;
      result: string;
      lastRunned: string | null;
      dateStart: string | null;
      dateEnd: string | null;
    };

    const current = this.record();
    const payload: JobProcess = {
      jobProcessId: this.jobProcessId() ?? EMPTY_GUID,
      websiteInstanceId: value.websiteInstanceId || null,
      name: value.name,
      description: value.description || null,
      tenantCode: value.tenantCode ?? null,
      parameters: value.parameters || null,
      execCmd: value.execCmd || null,
      user: value.user || null,
      status: value.status || null,
      notRestartable: value.notRestartable,
      errorMessage: value.errorMessage || null,
      lastRunned: value.lastRunned || null,
      result: value.result || null,
      dateCreate: current?.dateCreate ?? null,
      dateStart: value.dateStart || null,
      dateEnd: value.dateEnd || null,
    };

    this.isSaving.set(true);

    const id = this.jobProcessId();
    const save$ = id
      ? this.jobprocessService.updateJobProcess(id, payload)
      : this.jobprocessService.addJobProcess(payload);

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        this.isSaving.set(false);
        if (id) {
          this.record.set({ ...payload, ...saved });
          this.detailsEditing.set(false);
        } else if (saved?.jobProcessId && saved.jobProcessId !== EMPTY_GUID) {
          this.router.navigate(['/proces/jobs/editable', saved.jobProcessId]);
        } else {
          this.goBack();
        }
      },
      error: () => {
        this.isSaving.set(false);
      },
    });
  }

  // --- Page actions -------------------------------------------------------------

  goBack(): void {
    this.router.navigateByUrl('/proces/jobs');
  }

  onDelete(): void {
    const id = this.jobProcessId();
    if (!id || this.isRunning()) {
      return;
    }

    this.dialogService.open({
      width: '320px',
      title: 'Verwijderen?',
      message: 'Job verwijderen?',
      cancelText: 'Nee',
      confirmText: 'Ja',
      variant: 'error',
    });

    this.dialogService
      .confirmed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.jobprocessService
          .removeJobProcess(id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => this.goBack(),
          });
      });
  }

  onResetToWaiting(): void {
    const id = this.jobProcessId();
    const current = this.record();
    if (!id || !current) {
      return;
    }

    const payload: JobProcess = {
      ...current,
      status: 'Waiting',
      result: null,
      websiteInstanceId: null,
      errorMessage: null,
      lastRunned: null,
      dateStart: null,
      dateEnd: null,
    };

    this.isSaving.set(true);
    this.jobprocessService
      .updateJobProcess(id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.isSaving.set(false);
          const updated = { ...payload, ...saved };
          this.record.set(updated);
          this.patchForm(updated);
        },
        error: () => {
          this.isSaving.set(false);
        },
      });
  }
}
